/**
 * 分析バッチ実行のステータス型定義
 *
 * このファイルはバッチ分析のステータスに関する共通型定義を提供します。
 * フロントエンド全体で一貫したステータス処理を確保するため、
 * 他のファイルからimportして使用してください。
 */

/**
 * バッチ実行のステータス（フロントエンド用）
 */
export type BatchExecutionStatus =
  | 'CONFIRMING_START' // 開始確認中（クライアント専用。開始が確認できるまでの区間）
  | 'PENDING' // 実行待機中
  | 'RUNNING' // 実行中
  | 'RERUNNING' // 再実行中
  | 'SUCCEEDED' // 全て成功
  | 'FAILED' // 全て失敗
  | 'TIMEOUT' // タイムアウト
  | 'CANCELLED' // キャンセル済み
  | 'PARTIALLY_SUCCEEDED' // 一部成功・一部失敗

/**
 * 個別分析項目のステータス
 * （質問×モデルの組み合わせ単位）
 */
export type AnalysisItemStatus =
  | 'PENDING' // 実行待機中
  | 'RUNNING' // 実行中
  | 'SUCCEEDED' // 成功
  | 'FAILED' // 失敗

/**
 * ステータスの表示用情報
 */
export interface StatusDisplayInfo {
  label: string
  variant: 'default' | 'secondary' | 'destructive' | 'success' | 'warning'
  icon: string
}

/**
 * ステータス別の表示情報マップ
 */
export const STATUS_DISPLAY_MAP: Record<BatchExecutionStatus, StatusDisplayInfo> = {
  CONFIRMING_START: {
    label: '開始確認中',
    variant: 'secondary',
    icon: 'loader',
  },
  PENDING: {
    label: '待機中',
    variant: 'secondary',
    icon: 'clock',
  },
  RUNNING: {
    label: '実行中',
    variant: 'default',
    icon: 'loader',
  },
  RERUNNING: {
    label: '再実行中',
    variant: 'default',
    icon: 'loader',
  },
  SUCCEEDED: {
    label: '成功',
    variant: 'success',
    icon: 'check-circle',
  },
  FAILED: {
    label: '失敗',
    variant: 'destructive',
    icon: 'x-circle',
  },
  TIMEOUT: {
    label: 'タイムアウト',
    variant: 'warning',
    icon: 'clock',
  },
  CANCELLED: {
    label: 'キャンセル',
    variant: 'secondary',
    icon: 'x',
  },
  PARTIALLY_SUCCEEDED: {
    label: '一部成功',
    variant: 'warning',
    icon: 'alert-triangle',
  },
}

/**
 * 実行中かどうかを判定するヘルパー関数
 *
 * 注意: `CONFIRMING_START`（開始確認中）は**含めない**。
 * まだ開始が確認できていない実行を「実行中」として提示しないため（D1-a / FR-1-1）。
 * 同時実行数の計上には別途含める必要がある（計上側も実行中系の明示列挙で書かれているため）。
 */
export function isRunningStatus(status: BatchExecutionStatus): boolean {
  return status === 'PENDING' || status === 'RUNNING' || status === 'RERUNNING'
}

/**
 * 完了状態かどうかを判定するヘルパー関数
 */
export function isCompletedStatus(status: BatchExecutionStatus): boolean {
  return (
    status === 'SUCCEEDED' ||
    status === 'FAILED' ||
    status === 'TIMEOUT' ||
    status === 'CANCELLED' ||
    status === 'PARTIALLY_SUCCEEDED'
  )
}

/**
 * 成功状態かどうかを判定するヘルパー関数
 */
export function isSuccessStatus(status: BatchExecutionStatus): boolean {
  return status === 'SUCCEEDED' || status === 'PARTIALLY_SUCCEEDED'
}
